import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getActiveTimer, startTimer as apiStartTimer, stopTimer as apiStopTimer, cancelTimer as apiCancelTimer } from './api';
import { toast } from 'sonner';

const TimerContext = createContext();

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within TimerProvider');
  }
  return context;
};

export const TimerProvider = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [timerData, setTimerData] = useState({
    id: null,
    project_id: null,
    project_name: null,
    task_id: null,
    task_name: null,
    description: '',
    start_time: null,
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const lastSyncRef = useRef(null);

  // Calculate elapsed time from start_time
  const calculateElapsed = useCallback((startTime) => {
    if (!startTime) return 0;
    const start = new Date(startTime);
    const now = new Date();
    return Math.floor((now - start) / 1000);
  }, []);

  // Sync with server
  const syncWithServer = useCallback(async () => {
    try {
      const response = await getActiveTimer();
      const serverTimer = response.data;
      
      if (serverTimer) {
        setIsRunning(true);
        setTimerData({
          id: serverTimer.id,
          project_id: serverTimer.project_id,
          project_name: serverTimer.project_name,
          task_id: serverTimer.task_id,
          task_name: serverTimer.task_title,
          description: serverTimer.description || '',
          start_time: serverTimer.start_time,
        });
        setElapsedSeconds(calculateElapsed(serverTimer.start_time));
        lastSyncRef.current = Date.now();
      } else {
        setIsRunning(false);
        setTimerData({
          id: null,
          project_id: null,
          project_name: null,
          task_id: null,
          task_name: null,
          description: '',
          start_time: null,
        });
        setElapsedSeconds(0);
      }
    } catch (error) {
      console.error('Timer sync error:', error);
    } finally {
      setLoading(false);
    }
  }, [calculateElapsed]);

  // Initial sync on mount
  useEffect(() => {
    syncWithServer();
  }, [syncWithServer]);

  // Update elapsed time every second when running
  useEffect(() => {
    if (isRunning && timerData.start_time) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(calculateElapsed(timerData.start_time));
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timerData.start_time, calculateElapsed]);

  // Sync periodically and on visibility change (multi-tab support)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Resync when tab becomes visible
        syncWithServer();
      }
    };

    // Periodic sync every 30 seconds
    const syncInterval = setInterval(() => {
      if (isRunning) {
        syncWithServer();
      }
    }, 30000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', syncWithServer);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', syncWithServer);
      clearInterval(syncInterval);
    };
  }, [isRunning, syncWithServer]);

  // Start timer
  const startTimerFn = useCallback(async ({ project_id, project_name, task_id, task_name, description }) => {
    try {
      const response = await apiStartTimer({
        project_id,
        task_id: task_id || null,
        description: description || null,
      });
      
      const timer = response.data;
      setIsRunning(true);
      setTimerData({
        id: timer.id,
        project_id: timer.project_id,
        project_name: timer.project_name || project_name,
        task_id: timer.task_id,
        task_name: timer.task_title || task_name,
        description: timer.description || '',
        start_time: timer.start_time,
      });
      setElapsedSeconds(0);
      
      toast.success('Timer spuštěn');
      return timer;
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Už máte spuštěný timer. Nejprve ho zastavte.');
        // Resync to get current timer state
        await syncWithServer();
      } else {
        toast.error('Nepodařilo se spustit timer');
      }
      throw error;
    }
  }, [syncWithServer]);

  // Stop timer and create time entry
  const stopTimerFn = useCallback(async () => {
    try {
      const response = await apiStopTimer();
      const timeEntry = response.data;
      
      setIsRunning(false);
      setTimerData({
        id: null,
        project_id: null,
        project_name: null,
        task_id: null,
        task_name: null,
        description: '',
        start_time: null,
      });
      setElapsedSeconds(0);
      
      toast.success(`Záznam uložen: ${Math.round(timeEntry.duration_minutes)} min`);
      return timeEntry;
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Žádný aktivní timer');
        await syncWithServer();
      } else {
        toast.error('Nepodařilo se zastavit timer');
      }
      throw error;
    }
  }, [syncWithServer]);

  // Cancel timer without creating entry
  const cancelTimerFn = useCallback(async () => {
    try {
      await apiCancelTimer();
      
      setIsRunning(false);
      setTimerData({
        id: null,
        project_id: null,
        project_name: null,
        task_id: null,
        task_name: null,
        description: '',
        start_time: null,
      });
      setElapsedSeconds(0);
      
      toast.info('Timer zrušen');
    } catch (error) {
      if (error.response?.status === 404) {
        await syncWithServer();
      } else {
        toast.error('Nepodařilo se zrušit timer');
      }
    }
  }, [syncWithServer]);

  // Format elapsed time as HH:MM:SS
  const formatElapsedTime = useCallback(() => {
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [elapsedSeconds]);

  return (
    <TimerContext.Provider
      value={{
        isRunning,
        timerData,
        elapsedSeconds,
        loading,
        startTimer: startTimerFn,
        stopTimer: stopTimerFn,
        cancelTimer: cancelTimerFn,
        formatElapsedTime,
        syncWithServer,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export default TimerContext;
